import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../context/StoreContext';
import { Icon } from '../shared/ui/Icon';
import { formatPrice, getProductPrice, safeImage } from '../entities/product/helpers';

export function CheckoutPage() {
  const {
    cartItems, subtotal, user, settings, resourceStatus,
    go, handleCheckout,
  } = useStore();
  const deliveryAreas = useMemo(
    () => (Array.isArray(settings?.deliveryAreas) && settings.deliveryAreas.length
      ? settings.deliveryAreas
      : [{ id: 'tbilisi', slug: 'tbilisi', name: 'Tbilisi Standard Delivery', fee: 5 }]),
    [settings],
  );
  const paymentMethods = useMemo(() => {
    const configuredMethods = settings?.enabledPaymentMethods || settings?.paymentMethods || [];
    const normalizedMethods = configuredMethods
      .map((method) => typeof method === 'string'
        ? { id: method, label: method }
        : { ...method, id: method.id || method.value, label: method.label || method.name || method.id || method.value })
      .filter((method) => method.id && method.enabled !== false);
    return normalizedMethods.length ? normalizedMethods : [
      { id: 'cod', label: 'Cash on delivery', description: 'Pay when your order arrives.' },
      { id: 'card', label: 'Card', description: 'Secure online card payment.' },
    ];
  }, [settings]);
  const [deliveryAreaId, setDeliveryAreaId] = useState('tbilisi');
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const sectionRefs = useRef([]);
  const steps = ['Shipping', 'Payment', 'Review'];

  useEffect(() => {
    if (!deliveryAreas.some((area) => String(area.slug || area.id) === deliveryAreaId)) {
      setDeliveryAreaId(deliveryAreas[0] ? String(deliveryAreas[0].slug || deliveryAreas[0].id) : '');
    }
  }, [deliveryAreas, deliveryAreaId]);

  const selectedArea = deliveryAreas.find((area) => String(area.slug || area.id) === deliveryAreaId);
  const deliveryFee = Number(selectedArea?.fee || 0);

  if (resourceStatus.settings === 'loading') {
    return <div className="catalog-loading" aria-label="Loading checkout options"><div className="catalog-skeleton-heading"><i /><i /><i /></div></div>;
  }

  return (
    <section className="flow-page">
      <div className="breadcrumb">
        <button onClick={() => go('home')}>Home</button><i>/</i>
        <button onClick={() => go('cart')}>Your bag</button><i>/</i><span>Checkout</span>
      </div>
      <div className="flow-heading">
        <div><p className="eyebrow">Checkout</p><h1>Checkout</h1></div>
        <div className="checkout-steps" aria-label="Checkout progress">
          {steps.map((step, index) => (
            <button key={step} type="button" className={`checkout-step ${index === activeStep ? 'active' : completedSteps.includes(index) ? 'completed' : 'upcoming'}`} aria-current={index === activeStep ? 'step' : undefined} onClick={() => {
              setActiveStep(index);
              sectionRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              sectionRefs.current[index]?.focus({ preventScroll: true });
            }}>
              <b>{index < activeStep ? '✓' : `0${index + 1}`}</b> {step}
            </button>
          ))}
        </div>
      </div>
      {resourceStatus.settings === 'error' && (
        <p className="checkout-friendly-note">Using standard Tbilisi delivery and cash or card payment while store options reconnect.</p>
      )}
      <form className={`checkout-layout ${hasAttemptedSubmit ? 'has-attempted-submit' : ''}`} onInvalidCapture={() => setHasAttemptedSubmit(true)} onSubmit={handleCheckout}>
        <div className="checkout-fields">
          <section ref={(node) => { sectionRefs.current[0] = node; }} id="checkout-shipping" tabIndex="-1" className="form-section" onFocusCapture={() => setActiveStep(0)} onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)
              && [...event.currentTarget.querySelectorAll('input, select')].every((control) => control.checkValidity())) {
              setCompletedSteps((current) => current.includes(0) ? current : [...current, 0]);
            }
          }}>
            <p className="eyebrow">01 / DELIVERY DETAILS</p>
            <h2>Where should we send it?</h2>
            <div className="form-grid">
              <label>Full name<input name="name" required defaultValue={user?.name || ''} autoComplete="name" /></label>
              <label>Phone number<input name="phone" required type="tel" autoComplete="tel" /></label>
              <label>Email address<input name="email" required type="email" defaultValue={user?.email || ''} autoComplete="email" /></label>
              <label>City<input name="city" required autoComplete="address-level2" /></label>
              <label className="full-span">Street address<input name="address" required autoComplete="street-address" /></label>
              <label>Postal code<input name="postalCode" autoComplete="postal-code" /></label>
              <label>
                Delivery area
                <select name="deliveryLocation" required value={deliveryAreaId} onChange={(event) => setDeliveryAreaId(event.target.value)} disabled={!deliveryAreas.length}>
                  {deliveryAreas.map((area) => (
                    <option key={area.id || area.slug} value={area.slug || area.id}>{area.name} · {formatPrice(area.fee)}</option>
                  ))}
                </select>
              </label>
              <p className="full-span checkout-friendly-note">Choose a delivery area above. Free store pickup can be arranged by contacting us.</p>
            </div>
          </section>
          <section ref={(node) => { sectionRefs.current[1] = node; }} id="checkout-payment" tabIndex="-1" className="form-section" onFocusCapture={() => setActiveStep(1)} onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)
              && [...event.currentTarget.querySelectorAll('input, select')].every((control) => control.checkValidity())) {
              setCompletedSteps((current) => current.includes(1) ? current : [...current, 1]);
            }
          }}>
            <p className="eyebrow">02 / PAYMENT</p>
            <h2>Choose how to pay.</h2>
            {paymentMethods.length ? (
              <div className="payment-options">
                {paymentMethods.map((method, index) => (
                  <label key={method.id}>
                    <input type="radio" name="paymentMethod" value={method.id} defaultChecked={index === 0} />
                    <span className="payment-mark">{method.icon ? <Icon>{method.icon}</Icon> : null}</span>
                    <span><b>{method.label}</b>{method.description && <small>{method.description}</small>}</span>
                    <i />
                  </label>
                ))}
              </div>
            ) : <p className="checkout-friendly-note">Cash on delivery is available for this order.</p>}
          </section>
          <section ref={(node) => { sectionRefs.current[2] = node; }} id="checkout-review" tabIndex="-1" className="form-section checkout-review" onFocusCapture={() => setActiveStep(2)}>
            <p className="eyebrow">03 / REVIEW</p>
            <h2>Ready when you are.</h2>
            <p>Check your delivery details and order summary, then place your order securely.</p>
          </section>
        </div>
        <aside className="order-summary checkout-summary">
          <p className="eyebrow">YOUR ORDER</p>
          <h2>Order summary</h2>
          {cartItems.map((item) => (
            <div className="mini-order-item" key={item.id}>
              {safeImage(item.product) && <img src={safeImage(item.product)} alt="" />}
              <span>{item.product.title || item.product.name}<small>Qty {item.qty}</small></span>
              <b>{formatPrice(getProductPrice(item.product) * item.qty)}</b>
            </div>
          ))}
          <div><span>Subtotal</span><b>{formatPrice(subtotal)}</b></div>
          <div><span>Delivery</span><b>{selectedArea ? formatPrice(deliveryFee) : '—'}</b></div>
          <div className="summary-total"><span>Total</span><strong>{formatPrice(subtotal + deliveryFee)}</strong></div>
          <button
            className="button button-primary full-width"
            type="submit"
          >
            Place order <Icon>→</Icon>
          </button>
          {!user && <p className="signin-hint">Guest checkout · no account needed</p>}
        </aside>
      </form>
    </section>
  );
}
